import React from 'react';
import { AWSNetworkConfig } from './AWSNetworkConfig';
import { KeyValuePairs } from '../components/KeyValuePairs';
import { LoadingState } from '../components/LoadingState';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useAWSApplianceSetup } from './useAWSApplianceSetup';
import { FormIntroduction } from '../components/FormIntroduction';
import { CloudAttributesError } from '../components/CloudAttributesError';
import { VMBasedMigrationsConfig } from '../components/VMBasedMigrationsConfig';
import { Form, Select, Input, Checkbox, Button, Tooltip, Typography } from '../../../../components';
import { CommonApplianceConfiguration } from '../components/CommonApplianceConfiguration';
import { CONFIG } from '../../../../constants';

const { Link } = Typography;

// Flip to true to re-expose these fields to the user.
const ALLOW_DEDICATED_HOST_DEPLOYMNET = false;

export const AWSApplianceSetup = ({ cloudAccount, onSubmit, onCancel, submitting, isRedeploy }) => {
    const {
        form,
        initialLoading,
        loadingError,
        loadCloudAttributes,
        loadingRegionData,
        region,
        vpc,
        tenancy,
        encryptionEnabled,
        encryptionType,
        enableFsx,
        subnets,
        availableDedicatedHosts,
        instanceTags,
        setInstanceTags,
        handleRegionChange,
        handleVPCChange,
        handleSubnetChange,
        handleEncryptionTypeChange,
        handleTenancyChange,
        handleHostChange,
        handleFormSubmit,
        regionOptions,
        hostOptions,
        kmsKeyOptions,
        fsxFilesystemOptions,
        vpcOptions,
        instanceTypeOptions,
        commonConfigProps,
    } = useAWSApplianceSetup({ cloudAccount, onSubmit, isRedeploy });

    if (initialLoading) {
        return <LoadingState />;
    }

    if (loadingError) {
        return (
            <div className="aws-appliance-setup">
                <FormIntroduction />
                <CloudAttributesError error={loadingError} onRetry={loadCloudAttributes} />
            </div>
        );
    }

    return (
        <div className="aws-appliance-setup">
            <FormIntroduction />
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFormSubmit}
                initialValues={{
                    instanceType: 't3.large',
                    encryptionEnabled: false,
                    useProxyEnabled: true,
                    encryptionType: 'default_key',
                    tenancy: 'default',
                    hostId: 'default',
                    affinity: 'host',
                    sourceWorkersCount: 1,
                    enableFsx: false,
                    assignPublicIp: 'disable',
                    staticPrivateIp: '',
                }}
            >
                <Form.Item label="Region" name="region" rules={[{ required: true, message: 'Please select a region' }]}>
                    <Select
                        placeholder="Select a Region"
                        onChange={handleRegionChange}
                        loading={loadingRegionData}
                        showSearch={{
                            optionFilterProp: 'label',
                        }}
                        options={regionOptions}
                    />
                </Form.Item>

                {loadingRegionData && <div className="loading-indicator">Loading region data...</div>}
                <Form.Item label="Tenancy" name="tenancy" hidden={!ALLOW_DEDICATED_HOST_DEPLOYMNET}>
                    <Select
                        onChange={handleTenancyChange}
                        disabled={!region || loadingRegionData}
                        options={[
                            { value: 'default', label: 'Shared - Run a shared hardware instance' },
                            {
                                value: 'host',
                                label: 'Dedicated host - Launch this instance on a Dedicated host',
                            },
                        ]}
                    />
                </Form.Item>
                {ALLOW_DEDICATED_HOST_DEPLOYMNET && tenancy === 'host' && (
                    <>
                        <Form.Item
                            label="Host"
                            name="hostId"
                            rules={[{ required: true, message: 'Please select a host' }]}
                            help={
                                availableDedicatedHosts.length === 0
                                    ? 'No available dedicated hosts were found in this region.'
                                    : undefined
                            }
                            validateStatus={availableDedicatedHosts.length === 0 ? 'error' : undefined}
                        >
                            <Select
                                onChange={handleHostChange}
                                disabled={!region || loadingRegionData || !availableDedicatedHosts.length}
                                options={hostOptions}
                            />
                        </Form.Item>

                        <Form.Item label="Affinity" name="affinity">
                            <Select
                                disabled={!region || loadingRegionData}
                                options={[
                                    { value: 'host', label: 'Host' },
                                    { value: 'default', label: 'Off' },
                                ]}
                            />
                        </Form.Item>
                    </>
                )}
                <Form.Item
                    label={
                        <span>
                            Instance Type{' '}
                            <Tooltip title="This is the Instance Type used for the Migration Appliance. Prior to migration, you will be able to specify the instance type for your migration.">
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </span>
                    }
                    name="instanceType"
                    rules={[{ required: true, message: 'Please select an instance type' }]}
                >
                    <Select disabled={instanceTypeOptions.length <= 1} options={instanceTypeOptions} />
                </Form.Item>
                <Form.Item label="Storage">
                    <span>20 GiB</span>
                </Form.Item>

                <Form.Item name="encryptionEnabled" valuePropName="checked">
                    <Checkbox>
                        Enable Disk Encryption{' '}
                        <Tooltip title="If AWS EBS encryption is selected, then all drives on a physical partition will be encrypted.">
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </Checkbox>
                </Form.Item>

                {encryptionEnabled && (
                    <>
                        <Form.Item label="Disk(s) Encryption Type" name="encryptionType" rules={[{ required: true }]}>
                            <Select
                                onChange={handleEncryptionTypeChange}
                                options={[
                                    { value: 'default_key', label: 'At-rest with default encryption key' },
                                    { value: 'platform_key', label: 'At-rest with a platform-managed key' },
                                    { value: 'customer_key', label: 'At-rest with a customer-managed key' },
                                ]}
                            />
                        </Form.Item>

                        {encryptionType !== 'default_key' && (
                            <Form.Item
                                label="KMS Key"
                                name="kmsKey"
                                rules={[{ required: true, message: 'Please select a KMS key' }]}
                            >
                                <Select
                                    placeholder="Select a KMS Key"
                                    showSearch={{
                                        optionFilterProp: 'label',
                                    }}
                                    options={kmsKeyOptions}
                                />
                            </Form.Item>
                        )}
                    </>
                )}

                <Form.Item name="enableFsx" valuePropName="checked">
                    <Checkbox disabled={!region || loadingRegionData}>Configure FSxN Storage</Checkbox>
                </Form.Item>

                {enableFsx && (
                    <>
                        <Form.Item
                            label="FSx file systems"
                            name="fsxFilesystemId"
                            rules={[{ required: enableFsx, message: 'Please select an FSx file system' }]}
                        >
                            <Select
                                placeholder="Select One"
                                disabled={!region || loadingRegionData}
                                showSearch={{
                                    optionFilterProp: 'label',
                                }}
                                options={fsxFilesystemOptions}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Username"
                            name="fsxUsername"
                            rules={[{ required: enableFsx, message: 'FSx username is required' }]}
                        >
                            <Input placeholder="Username" autoComplete="off" />
                        </Form.Item>

                        <Form.Item
                            label="Password"
                            name="fsxPassword"
                            rules={[{ required: enableFsx, message: 'FSx password is required' }]}
                        >
                            <Input.Password placeholder="Password" autoComplete="new-password" />
                        </Form.Item>
                    </>
                )}
                <Form.Item label="VPC" name="vpc" rules={[{ required: true, message: 'Please select a VPC' }]}>
                    <Select
                        placeholder="Select a VPC"
                        onChange={handleVPCChange}
                        disabled={!region || loadingRegionData}
                        showSearch={{
                            optionFilterProp: 'label',
                        }}
                        options={vpcOptions}
                    />
                </Form.Item>
                <AWSNetworkConfig
                    networks={subnets}
                    disabled={!vpc || loadingRegionData}
                    onSubnetChange={handleSubnetChange}
                />
                <Form.Item
                    label={
                        <span>
                            Instance Tags{' '}
                            <Tooltip
                                title={
                                    <>
                                        A tag consists of a key-value pair. Maximum number of tags per resource: 50.
                                        Maximum key length: 127. Maximum value length: 255. Tag keys and values are case
                                        sensitive. Do not use the <code>aws:</code> prefix in your tag names or values —
                                        it is reserved for AWS use. Allowed characters: letters, spaces and numbers in
                                        UTF-8, plus + - = . _ : / @
                                    </>
                                }
                            >
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </span>
                    }
                >
                    <KeyValuePairs
                        dataSource={instanceTags}
                        onChange={setInstanceTags}
                        allowEmptyValue={true}
                        keyPlaceholder="key"
                        valuePlaceholder="value"
                        instructionMessage="Click the + icon or press Enter to add a tag. You can add up to 47 tags (3 tags — BuildNumber, Name and RiverMeadowId — will be added automatically)."
                        limit={50}
                        keyValidation={{ maxLength: 127 }}
                        valueValidation={{ maxLength: 255 }}
                    />
                </Form.Item>
                <Form.Item
                    label={
                        <span>
                            Instance Name{' '}
                            <Tooltip title="Instance Name of the Migration Appliance.">
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </span>
                    }
                    name="instanceName"
                    rules={[{ required: true, message: 'Please enter an instance name' }]}
                >
                    <Input placeholder="Enter an Instance Name" />
                </Form.Item>

                <Form.Item label="Security Groups">
                    <Link
                        href={`${CONFIG.AWS_DOCS_LINK}$securitygroups`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        View RiverMeadow's Security Group process
                    </Link>
                </Form.Item>

                <VMBasedMigrationsConfig supportsMultipleSourceWorkers />
                <CommonApplianceConfiguration {...commonConfigProps} />
                <div className="modal-footer">
                    <Button type="default" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={submitting}>
                        Initiate Migration Appliance checks
                    </Button>
                </div>
            </Form>
        </div>
    );
};
